/**
 * Per-person memory for the kit "More" section — issue #1127 (D219, D223 Q2).
 *
 * Every screen opens simple; a "More" row reveals the rarely-used parts, and
 * a person who opened it finds it open again on that screen. The choice is
 * kept in this browser's `localStorage`, keyed by user id:
 *
 *   nw_more:<screen>:<userId>  =  "1"   (absent means closed)
 *
 * WHY THE USER ID IS IN THE KEY. Every other stored view preference in this
 * app is a bare global key (`nw_text_size`, `nw_dash_*`, …), so a second
 * person on the same phone inherits the first person's choices. This key
 * cannot be built without a user id (`moreKey` throws), so no shared key can
 * exist. The value is a boolean and the key names no screen content, so the
 * D207.3 failure (milestone text copied into `localStorage` and read by the
 * next account on a shared tab) cannot recur here.
 *
 * WHY NOT THE SERVER. D223 Q2: no `users` column and no migration, because a
 * code rollback (D222) would leave the column behind.
 *
 * The storage pattern is `lib/basemap.js`: every access in try/catch, because
 * Safari private mode throws on access and a broken preference must never
 * stop a screen drawing.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * The closed list of screen names. A new screen is added here on purpose;
 * `moreKey` throws on any name not listed.
 */
export const KNOWN_MORE_SCREENS = ["learn", "learn-topics", "record", "career", "settings"];

const PREFIX = "nw_more";

function hasId(userId) {
  return userId !== null && userId !== undefined && userId !== "" && userId !== false;
}

export function moreKey(screen, userId) {
  if (!screen) throw new Error("moreKey: a screen is required");
  if (!hasId(userId)) throw new Error("moreKey: a userId is required, never a shared key");
  if (!KNOWN_MORE_SCREENS.includes(screen)) {
    throw new Error(`moreKey: unknown screen "${screen}"; add it to KNOWN_MORE_SCREENS`);
  }
  return `nw_more:${screen}:${userId}`;
}

/** True only when the stored value is exactly "1". No user, or an unreadable store, reads closed. */
export function readMore(screen, userId) {
  if (!hasId(userId)) return false;
  try {
    return window.localStorage.getItem(moreKey(screen, userId)) === "1";
  } catch {
    return false;
  }
}

/** Remember open ("1") or forget (remove the key). Returns whether the store was touched. */
export function writeMore(screen, userId, open) {
  if (!hasId(userId)) return false;
  try {
    const key = moreKey(screen, userId);
    if (open) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * The last Guides topic a person opened — issue #1128 (D221 O2). "Your last
 * topic" on the Guides section is shown first, the other topics sit behind
 * "All eight topics". A sibling of the More choice, not a new pattern: one
 * key per person, `nw_learn_topic:<userId>`, never a bare global key, so a
 * second person on the same phone does not inherit the first one's topic.
 * The value is the topic's key (`batting`, `field-placement`), which names a
 * fixed list in the page and no personal content.
 *
 * Every access is in try/catch, like the More helpers above.
 */
const TOPIC_PREFIX = "nw_learn_topic";

export function learnTopicKey(userId) {
  if (!hasId(userId)) throw new Error("learnTopicKey: a userId is required, never a shared key");
  return `${TOPIC_PREFIX}:${userId}`;
}

/** The stored topic key, or `null` with no user, nothing stored, or an unreadable store. */
export function readLearnTopic(userId) {
  if (!hasId(userId)) return null;
  try {
    const v = window.localStorage.getItem(learnTopicKey(userId));
    return v ? v : null;
  } catch {
    return null;
  }
}

/** Remember the topic. Returns whether the store was touched. */
export function writeLearnTopic(userId, topic) {
  if (!hasId(userId) || !topic) return false;
  try {
    window.localStorage.setItem(learnTopicKey(userId), String(topic));
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove every `nw_more:*:<userId>` key, and the person's last Guides topic.
 * Used when an account is deleted: the choice survives sign-out (D219,
 * Andreas's ruling) and goes with the account.
 */
export function clearMoreForUser(userId) {
  if (!hasId(userId)) return;
  try {
    const store = window.localStorage;
    store.removeItem(learnTopicKey(userId));
    const suffix = `:${userId}`;
    const doomed = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(`${PREFIX}:`) && key.endsWith(suffix)) {
        const screen = key.slice(PREFIX.length + 1, key.length - suffix.length);
        if (KNOWN_MORE_SCREENS.includes(screen)) doomed.push(key);
      }
    }
    doomed.forEach((key) => store.removeItem(key));
  } catch {
    /* an unreadable store has nothing to clear */
  }
}

/**
 * `[open, setOpen]` for one screen, backed by the store. The user comes from
 * `AuthContext` unless one is passed. Signed out or still loading: state
 * only, nothing read or written.
 */
export function useMoreState(screen, userIdOverride) {
  const auth = useAuth();
  const ctxId = auth?.user && auth.user.id !== undefined ? auth.user.id : null;
  const userId = userIdOverride !== undefined ? userIdOverride : ctxId;
  const [open, setOpenState] = useState(() => readMore(screen, userId));

  useEffect(() => {
    // The user can arrive after first paint (AuthContext probes /auth/me).
    setOpenState(readMore(screen, userId));
  }, [screen, userId]);

  const setOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(open) : next;
      writeMore(screen, userId, value);
      setOpenState(value);
    },
    [screen, userId, open],
  );

  return [open, setOpen];
}
